// src/utils/toastService.ts

import { toast, ToastOptions, Id } from "react-toastify";
import { AxiosError, AxiosResponse } from "axios";

/**
 * Default toast configuration for consistent styling
 */
const defaultOptions: ToastOptions = {
  position: "top-center",
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: "colored",
};

/**
 * Toast service for centralized notification management
 */
export const toastService = {
  /**
   * Show a success toast
   */
  success: (message: string, options?: ToastOptions): Id => {
    return toast.success(message, { ...defaultOptions, ...options });
  },

  /**
   * Show an error toast
   */
  error: (message: string, options?: ToastOptions): Id => {
    return toast.error(message, { ...defaultOptions, autoClose: 5000, ...options });
  },

  /**
   * Show a warning toast
   */
  warning: (message: string, options?: ToastOptions): Id => {
    return toast.warning(message, { ...defaultOptions, ...options });
  },

  /**
   * Show an info toast
   */
  info: (message: string, options?: ToastOptions): Id => {
    return toast.info(message, { ...defaultOptions, ...options });
  },

  /**
   * Show a loading toast (returns ID to update later)
   */
  loading: (message: string, options?: ToastOptions): Id => {
    return toast.loading(message, { ...defaultOptions, autoClose: false, ...options });
  },

  /**
   * Update an existing toast
   */
  update: (toastId: Id, options: ToastOptions & { render: string; type: "success" | "error" | "info" | "warning"; isLoading?: boolean }): void => {
    toast.update(toastId, {
      ...defaultOptions,
      ...options,
    });
  },

  /**
   * Dismiss a specific toast or all toasts
   */
  dismiss: (toastId?: Id): void => {
    toast.dismiss(toastId);
  },
};

/**
 * Extract message from API response data
 * Handles various response formats from the server
 */
export const extractResponseMessage = (data: any): string | null => {
  if (!data) return null;

  // Check common message fields
  if (typeof data === "string") return data;
  if (data.message) return data.message;
  if (data.msg) return data.msg;
  if (data.success && typeof data.success === "string") return data.success;
  if (data.data?.message) return data.data.message;

  return null;
};

/**
 * Extract error message from API error response
 * Handles various error response formats
 */
export const extractErrorMessage = (error: unknown): string => {
  // Handle Axios errors
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError<{
      message?: string;
      error?: string | { message?: string };
      errors?: any[] | Record<string, string[]>;
      msg?: string;
    }>;

    // Get response data
    const data = axiosError.response?.data;

    if (data) {
      // Check for message field
      if (data.message) {
        return data.message;
      }

      // Check for msg field
      if (data.msg) {
        return data.msg;
      }

      // Check for error field
      if (data.error) {
        if (typeof data.error === "string") {
          return data.error;
        }
        if (typeof data.error === "object" && data.error.message) {
          return data.error.message;
        }
      }

      // Check for errors array (validation errors)
      if (data.errors) {
        if (Array.isArray(data.errors)) {
          const messages = data.errors
            .map((e: any) => e.message || e.msg || (typeof e === "string" ? e : null))
            .filter(Boolean);
          if (messages.length > 0) {
            return messages.join(", ");
          }
        }

        // Check for errors object (field-level validation)
        if (typeof data.errors === "object" && !Array.isArray(data.errors)) {
          const errorMessages = Object.entries(data.errors)
            .map(([field, msgs]) => {
              if (Array.isArray(msgs)) {
                return msgs.join(", ");
              }
              return typeof msgs === "string" ? msgs : null;
            })
            .filter(Boolean);
          if (errorMessages.length > 0) {
            return errorMessages.join(". ");
          }
        }
      }
    }

    // Network error
    if (axiosError.code === "ERR_NETWORK") {
      return "Network error. Please check your internet connection.";
    }

    // Request timeout
    if (axiosError.code === "ECONNABORTED") {
      return "Request timed out. Please try again.";
    }

    // Status code based messages as fallback
    if (axiosError.response?.status) {
      switch (axiosError.response.status) {
        case 400:
          return "Invalid request. Please check your input.";
        case 401:
          return "Your session has expired. Please log in again.";
        case 403:
          return "You don't have permission to perform this action.";
        case 404:
          return "The requested resource was not found.";
        case 409:
          return "Conflict. This resource already exists.";
        case 413:
          return "File too large. Please upload a smaller file.";
        case 415:
          return "Unsupported file type. Please check the file format.";
        case 422:
          return "Validation error. Please check your input.";
        case 429:
          return "Too many requests. Please wait a moment and try again.";
        case 500:
          return "Server error. Please try again later.";
        case 502:
          return "Server is temporarily unavailable. Please try again.";
        case 503:
          return "Service unavailable. Please try again later.";
        default:
          return `Error ${axiosError.response.status}: Something went wrong.`;
      }
    }

    // If we have an axios error but no response (network issue)
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  // Handle regular Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === "string") {
    return error;
  }

  // Fallback for unknown error types
  return "An unexpected error occurred. Please try again.";
};

/**
 * Handle API response and show appropriate toast
 * Returns the response for chaining
 */
export const handleApiResponse = <T>(response: AxiosResponse<T>): AxiosResponse<T> => {
  const message = extractResponseMessage(response.data);

  // Only show success toast if there's a meaningful message from the server
  // and it's not a GET request (we don't want to toast on every data fetch)
  if (message && response.config.method !== "get") {
    toastService.success(message);
  }

  return response;
};

/**
 * Handle API error and show appropriate toast
 * Re-throws the error for proper error handling
 */
export const handleApiError = (error: unknown): never => {
  const message = extractErrorMessage(error);
  toastService.error(message);
  throw error;
};

/**
 * Promise-based toast for async operations
 * Shows loading, then success/error based on promise result
 */
export const toastPromise = <T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error?: string | ((error: unknown) => string);
  }
): Promise<T> => {
  toast.promise(
    promise,
    {
      pending: messages.loading,
      success: {
        render: ({ data }) => {
          if (typeof messages.success === "function") {
            return messages.success(data as T);
          }
          // Try to get message from response
          const responseMessage = extractResponseMessage(data);
          return responseMessage || messages.success;
        },
      },
      error: {
        render: ({ data }) => {
          if (messages.error) {
            if (typeof messages.error === "function") {
              return messages.error(data);
            }
            return messages.error;
          }
          return extractErrorMessage(data);
        },
      },
    },
    defaultOptions
  );
  
  // Return the original promise to maintain type safety
  return promise;
};

export default toastService;
